/**
 * The files half of an admin console: drop or pick files, rename them, remove
 * them. One row per file, and the row is the title — the list a clinician sees
 * in the app is a list of titles, so that is what is edited here.
 *
 * Uploads happen on drop, deletions on save. See the note in configFilesApi on
 * why uploading immediately is the right half of that asymmetry; the other half
 * is `onRemoveFile`, which hands the caller the blob path of a row the user took
 * out so it can be deleted once the configuration that referenced it is saved.
 * Deleting the blob here instead would leave the stored configuration pointing
 * at a file that no longer exists whenever the admin walks away without saving.
 */
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Trash2, UploadCloud } from 'lucide-react';
import UploadErrors from './UploadErrors';
import DeleteConfirmationDialog from './dialogs/DeleteConfirmationDialog';
import { describeAccept, formatFileSize, partitionFiles } from '../utils/file-validation';
import {
  CONFIG_FILE_ACCEPT,
  CONFIG_FILE_MAX_BYTES,
  configFileUrl,
  uploadConfigFile,
  type ConfigFileRef,
} from '../services/configFilesApi';
import './ConfigFilesEditor.scss';

interface Props {
  /** AppConfig coordinates — they decide the storage folder. */
  appName: string;
  configName: string;
  hospital: string;
  files: ConfigFileRef[];
  onChange: (next: ConfigFileRef[]) => void;
  /**
   * Called with the blob path of a removed file, for the caller to delete once
   * the configuration is saved. Rows with no path (a built-in) never reach it.
   */
  onRemoveFile?: (path: string) => void;
  /** Resolves the "open" link. Defaults to the storage download URL. */
  hrefFor?: (file: ConfigFileRef) => string | undefined;
  disabled?: boolean;
  /** Shown above the list; the caller words it for its own screen. */
  emptyText?: string;
}

const fileNameOf = (file: ConfigFileRef): string =>
  file.path ? file.path.split('/').pop() || file.path : 'קובץ מובנה באפליקציה';

const defaultHrefFor = (file: ConfigFileRef): string | undefined =>
  file.path ? configFileUrl(file.path) : undefined;

const ConfigFilesEditor: React.FC<Props> = ({
  appName,
  configName,
  hospital,
  files,
  onChange,
  onRemoveFile,
  hrefFor = defaultHrefFor,
  disabled = false,
  emptyText = 'לא הוגדרו קבצים.',
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const onDrop = useCallback(
    async (dropped: File[], refused: { file: File }[]) => {
      if (disabled || !hospital) return;

      // Two sources of rejection, and both have to be spoken about: dropzone
      // filters by type and size before onDrop is called, and partitionFiles
      // catches what it does not (an empty file) with the same wording every
      // other picker in the CMS uses.
      const { accepted, rejections } = partitionFiles(dropped, {
        accept: CONFIG_FILE_ACCEPT,
        maxSize: CONFIG_FILE_MAX_BYTES,
      });
      // Replace rather than append, so a message about a file the user has
      // since replaced cannot linger.
      setErrors([
        ...(refused || []).map((r) => `הקובץ "${r.file.name}" אינו נתמך או גדול מדי.`),
        ...rejections.map((r) => r.message),
      ]);
      if (accepted.length === 0) return;

      setUploading((n) => n + accepted.length);
      const uploaded: ConfigFileRef[] = [];
      const failures: string[] = [];

      // Sequential: each upload is a whole document, and a hospital connection
      // that struggles with one 20 MB file will not do better with four at once.
      for (const file of accepted) {
        try {
          const result = await uploadConfigFile(appName, configName, hospital, file);
          // The stored blob name is sanitized ASCII plus a timestamp, so a
          // Hebrew-named file would otherwise land in the list as "file-173…".
          // The name the user picked is the title they meant.
          uploaded.push({ title: file.name.replace(/\.[^.]+$/, ''), path: result.path });
        } catch (err: any) {
          console.error('Config file upload failed:', err);
          failures.push(err?.serverError?.hebMessage || `העלאת הקובץ "${file.name}" נכשלה.`);
        } finally {
          setUploading((n) => n - 1);
        }
      }

      if (failures.length > 0) setErrors((prev) => [...prev, ...failures]);
      if (uploaded.length > 0) onChange([...files, ...uploaded]);
    },
    [appName, configName, hospital, files, onChange, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: CONFIG_FILE_ACCEPT,
    maxSize: CONFIG_FILE_MAX_BYTES,
    disabled: disabled || !hospital,
  });

  const setTitle = (index: number, title: string) => {
    onChange(files.map((f, i) => (i === index ? { ...f, title } : f)));
  };

  const confirmRemove = () => {
    if (deleteIndex === null) return;
    const removed = files[deleteIndex];
    if (removed?.path && onRemoveFile) onRemoveFile(removed.path);
    onChange(files.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  const acceptHint = `${describeAccept(CONFIG_FILE_ACCEPT)} · עד ${formatFileSize(CONFIG_FILE_MAX_BYTES)} לקובץ`;

  return (
    <div className="config-files-editor">
      {!disabled && (
        <div
          {...getRootProps()}
          className={`cfe-dropzone${isDragActive ? ' active' : ''}${hospital ? '' : ' disabled'}`}
          aria-label="העלאת קבצים"
        >
          <input {...getInputProps()} />
          <UploadCloud className="cfe-dropzone-icon" size={32} aria-hidden="true" />
          <div>
            <p className="cfe-dropzone-title">
              {uploading > 0 ? `מעלה ${uploading} קבצים...` : 'גררו קבצים לכאן או לחצו לבחירה'}
            </p>
            <p className="cfe-dropzone-subtitle">{acceptHint}</p>
          </div>
        </div>
      )}

      <UploadErrors messages={errors} onDismiss={() => setErrors([])} hint={acceptHint} />

      {files.length === 0 ? (
        <p className="cfe-empty">{emptyText}</p>
      ) : (
        <ul className="cfe-list">
          {files.map((file, index) => {
            const href = hrefFor(file);
            return (
              <li className="cfe-row" key={file.path || file.builtin || index}>
                <FileText className="cfe-row-icon" size={22} aria-hidden="true" />
                <div className="cfe-row-main">
                  <input
                    type="text"
                    className="cfe-title-input"
                    value={file.title}
                    disabled={disabled}
                    placeholder="כותרת הקובץ"
                    aria-label={`כותרת הקובץ, שורה ${index + 1}`}
                    onChange={(e) => setTitle(index, e.target.value)}
                  />
                  <span className="cfe-row-filename" title={fileNameOf(file)}>
                    {fileNameOf(file)}
                  </span>
                </div>
                {href && (
                  <a className="cfe-row-link" href={href} target="_blank" rel="noopener noreferrer">
                    פתח
                  </a>
                )}
                {!disabled && (
                  <button
                    type="button"
                    className="cfe-row-remove"
                    onClick={() => setDeleteIndex(index)}
                    aria-label={`הסר את ${file.title || fileNameOf(file)}`}
                    title="הסר קובץ"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <DeleteConfirmationDialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={confirmRemove}
        title={`להסיר את "${(deleteIndex !== null && files[deleteIndex]?.title) || 'הקובץ'}"?`}
        showError={false}
        errorMessage=""
        onCloseError={() => {}}
      />
    </div>
  );
};

export default ConfigFilesEditor;
